import { Schema } from "@schema-types/types";
import { isJourneyTerminalStep } from "@schema-types/types/journeyTerminalStep";
import { UI } from "@forms/form-render/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useUserJourneyContext } from "@/context/UserJourneyContext";
import { getCompleteEnquiryCookie, renameEnquiryCookieToComplete } from "@/data/enquiries/cookie";
import { isFirstStepInPipeline } from "@/data/journeyPipeline";

function getTurnstileSiteKey(): string | undefined {
  const key = window.__TURNSTILE__?.siteKey;
  return key && key.length > 0 ? key : undefined;
}

interface StepProps {
  journeySchema: Schema.JourneySchema;
  currentStepId: string;
  readOnly?: boolean;
}

export const Step = ({ journeySchema, currentStepId, readOnly }: StepProps) => {
  const { enquiry, updateStepData, isSubmitting, journeyVersion, turnstileTokenRef, turnstileFeatureEnabled } = useUserJourneyContext();
  const navigate = useNavigate();

  // Find step by displayId first, then fall back to id
  const currentStep = journeySchema.steps.find((step) => step.displayId === currentStepId || step.id === currentStepId);

  if (!currentStep) return <div>Journey is empty</div>;

  const isTerminalStep = isJourneyTerminalStep(currentStep);

  useEffect(() => {
    if (isTerminalStep) {
      renameEnquiryCookieToComplete();
    }
  }, [isTerminalStep]);

  const handleSubmit = async (stepId: string, data: Record<string, string | string[]>) => {
    const nextStepId = await updateStepData(stepId, data);
    if (nextStepId) {
      const nextStep = journeySchema.steps.find((step) => step.id === nextStepId);
      if (nextStep) {
        navigate(`/${nextStep.displayId}`);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  // If enquiry doesn't exist yet (before first submission), use empty data
  const formData = enquiry?.stepData.find((step) => step.stepId === currentStep.id)?.data || {};

  // Check if enquiry exists and has data for this step
  const hasExistingData =
    enquiry !== null &&
    enquiry.stepData.some((step) => step.stepId === currentStep.id && Object.keys(step.data || {}).length > 0);

  const isFirstStep = isFirstStepInPipeline(journeySchema, currentStepId);
  const isStartingNewEnquiry = enquiry === null;
  const turnstileSiteKey = getTurnstileSiteKey();
  const showTurnstile = !readOnly && isFirstStep && isStartingNewEnquiry && turnstileSiteKey && turnstileFeatureEnabled;
  const [turnstileVerification, setTurnstileVerification] = useState<"pending" | "success" | "error">("pending");
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const prevShowTurnstileRef = useRef(false);

  useEffect(() => {
    if (showTurnstile && !prevShowTurnstileRef.current) {
      setTurnstileVerification("pending");
      turnstileTokenRef.current = null;
    }
    prevShowTurnstileRef.current = showTurnstile;
  }, [showTurnstile]);

  const handleTurnstileRetry = () => {
    turnstileTokenRef.current = null;
    setTurnstileVerification("pending");
    turnstileRef.current?.reset();
  };

  const renderBeforeActions =
    showTurnstile && turnstileSiteKey ? (
      <div className="flex justify-center">
        <Turnstile
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          onSuccess={(token) => {
            turnstileTokenRef.current = token;
            setTurnstileVerification("success");
          }}
          onError={() => {
            turnstileTokenRef.current = null;
            setTurnstileVerification("error");
          }}
          onExpire={() => {
            turnstileTokenRef.current = null;
            setTurnstileVerification("pending");
            turnstileRef.current?.reset();
          }}
          style={{ maxHeight: "65px", maxWidth: "300px" }}
          options={{ size: "invisible", appearance: "interaction-only" }}
        />
      </div>
    ) : undefined;

  const disableNextUntilTurnstile = showTurnstile && turnstileVerification !== "success";
  const turnstileFeedback = showTurnstile
    ? { state: turnstileVerification, onRetry: handleTurnstileRetry }
    : undefined;

  const enquiryIdForPortalInvite =
    isTerminalStep ? enquiry?.id ?? getCompleteEnquiryCookie()?.enquiryId : enquiry?.id;

  return (
    <UI.StepRenderer
      showProgress={true}
      journey={journeySchema}
      step={currentStep}
      journeyVersion={journeyVersion ?? "dev"}
      onNextClick={handleSubmit}
      onBackClick={handleBack}
      data={formData}
      journeyData={enquiry?.stepData || []}
      isLoading={isSubmitting}
      hasExistingData={hasExistingData}
      enquiryId={enquiryIdForPortalInvite}
      renderBeforeActions={renderBeforeActions}
      disableNextUntilTurnstile={disableNextUntilTurnstile}
      turnstileFeedback={turnstileFeedback}
      readOnly={readOnly}
      evaluationTimeZoneId={enquiry?.clientTimeZoneId}
    />
  );
};

